<template>
  <div class="analytics-panel">

    <!-- Overview cards -->
    <div class="analytics-section">
      <div class="analytics-section-title">API Overview</div>
      <div class="stat-cards">
        <div class="stat-card">
          <div class="stat-card-value">{{ formatNumber(overview.totalRequests) }}</div>
          <div class="stat-card-label">total requests</div>
        </div>
        <div class="stat-card stat-card-success">
          <div class="stat-card-value">{{ formatNumber(overview.successCount) }}</div>
          <div class="stat-card-label">successful</div>
        </div>
        <div class="stat-card stat-card-error">
          <div class="stat-card-value">{{ formatNumber(overview.errorCount) }}</div>
          <div class="stat-card-label">errors</div>
        </div>
        <div class="stat-card">
          <div class="stat-card-value">{{ overview.avgDurationMs }}<span class="stat-unit">ms</span></div>
          <div class="stat-card-label">avg latency</div>
        </div>
      </div>
    </div>

    <!-- Usage by API key -->
    <div class="analytics-section">
      <div class="analytics-section-title">Usage by API Key</div>
      <div v-if="keyBreakdown.length === 0" class="analytics-empty">No API requests recorded yet.</div>
      <table v-else class="analytics-table">
        <thead>
          <tr>
            <th>key name</th>
            <th>requests</th>
            <th>errors</th>
            <th>avg latency</th>
            <th>error rate</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, i) in keyBreakdown" :key="i">
            <td>
              <span class="key-name-badge">{{ row.api_key_name }}</span>
            </td>
            <td>{{ formatNumber(row.request_count) }}</td>
            <td>
              <span :class="parseInt(row.error_count) > 0 ? 'error-text' : ''">
                {{ formatNumber(row.error_count || 0) }}
              </span>
            </td>
            <td>{{ row.avg_duration_ms != null ? Math.round(row.avg_duration_ms) + ' ms' : '–' }}</td>
            <td>
              <span :class="errorRate(row) > 10 ? 'error-text' : ''">
                {{ errorRate(row).toFixed(1) }}%
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Operations breakdown -->
    <div class="analytics-section">
      <div class="analytics-section-title">Operations Breakdown</div>
      <div v-if="operationBreakdown.length === 0" class="analytics-empty">No API requests recorded yet.</div>
      <table v-else class="analytics-table">
        <thead>
          <tr>
            <th>operation</th>
            <th>resource type</th>
            <th>count</th>
            <th>avg latency</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, i) in operationBreakdown" :key="i">
            <td>
              <span class="op-badge" :class="'op-' + opColor(row.operation)">{{ row.operation }}</span>
            </td>
            <td>{{ row.resource_type || '–' }}</td>
            <td>{{ formatNumber(row.count) }}</td>
            <td>{{ row.avg_duration_ms != null ? Math.round(row.avg_duration_ms) + ' ms' : '–' }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Activity over time -->
    <div class="analytics-section">
      <div class="analytics-section-title">API Activity Over Time</div>
      <div v-if="activityDays.length === 0" class="analytics-empty">No API requests recorded yet.</div>
      <div v-else class="histogram-wrap">
        <div class="histogram">
          <div class="histogram-y-axis">
            <span class="histogram-y-label">{{ maxDayCount }}</span>
            <span class="histogram-y-label">{{ Math.round(maxDayCount / 2) }}</span>
            <span class="histogram-y-label">0</span>
          </div>
          <div class="histogram-bars">
            <div class="histogram-col" v-for="day in activityDays" :key="day.date">
              <div class="histogram-stack" :title="day.total + ' total'">
                <div
                  v-for="(seg, si) in day.segments"
                  :key="si"
                  class="histogram-segment"
                  :style="{ height: segmentHeight(seg.count), background: segmentColor(si) }"
                  :title="seg.name + ': ' + seg.count"
                ></div>
              </div>
              <div class="histogram-x-label">{{ formatDate(day.date) }}</div>
            </div>
          </div>
        </div>
        <div class="chart-legend">
          <span class="legend-item" v-for="(name, idx) in uniqueKeyNames" :key="name">
            <span class="legend-dot" :style="{ background: segmentColor(idx) }"></span>
            {{ name }}
          </span>
        </div>
      </div>
    </div>

    <!-- Top endpoints -->
    <div class="analytics-section">
      <div class="analytics-section-title">Top Endpoints</div>
      <div v-if="topEndpoints.length === 0" class="analytics-empty">No API requests recorded yet.</div>
      <table v-else class="analytics-table">
        <thead>
          <tr>
            <th>method</th>
            <th>resource</th>
            <th>operation</th>
            <th>count</th>
            <th>avg latency</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, i) in topEndpoints" :key="i">
            <td><span class="method-badge" :class="'method-' + (row.method || '').toLowerCase()">{{ row.method }}</span></td>
            <td>{{ row.resource_id || row.resource_type || '–' }}</td>
            <td>
              <span class="op-badge" :class="'op-' + opColor(row.operation)">{{ row.operation || '–' }}</span>
            </td>
            <td>{{ formatNumber(row.count) }}</td>
            <td>{{ row.avg_duration_ms != null ? Math.round(row.avg_duration_ms) + ' ms' : '–' }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Recent requests -->
    <div class="analytics-section">
      <div class="analytics-section-title">Recent Requests</div>
      <div v-if="recentRequests.length === 0" class="analytics-empty">No API requests recorded yet.</div>
      <table v-else class="analytics-table analytics-table-compact">
        <thead>
          <tr>
            <th>time</th>
            <th>key</th>
            <th>method</th>
            <th>path</th>
            <th>status</th>
            <th>latency</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, i) in recentRequests" :key="i" :class="row.status_code >= 400 ? 'row-error' : ''">
            <td class="time-cell">{{ formatTime(row.created_at) }}</td>
            <td><span class="key-name-badge">{{ row.api_key_name }}</span></td>
            <td><span class="method-badge" :class="'method-' + (row.method || '').toLowerCase()">{{ row.method }}</span></td>
            <td class="path-cell">{{ row.path }}</td>
            <td>
              <span class="status-badge" :class="row.status_code >= 400 ? 'status-error' : 'status-ok'">
                {{ row.status_code }}
              </span>
            </td>
            <td>{{ row.duration_ms != null ? row.duration_ms + ' ms' : '–' }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>

import axios from 'axios'

const COLORS = ['#4a9eff', '#6cc644', '#e8a020', '#e05050', '#9b59b6', '#1abc9c', '#e67e22', '#3498db', '#e74c3c', '#2ecc71']

export default {
  name: 'AnalyticsApi',
  props: {
    application: String,
    from: String,
    to: String,
    refreshKey: Number
  },
  data () {
    return {
      overview: { totalRequests: 0, successCount: 0, errorCount: 0, avgDurationMs: 0 },
      keyBreakdown: [],
      operationBreakdown: [],
      activityRaw: [],
      topEndpoints: [],
      recentRequests: []
    }
  },
  computed: {
    uniqueKeyNames () {
      const names = new Set()
      for (const row of this.activityRaw) {
        names.add(row.api_key_name)
      }
      return [...names]
    },
    activityDays () {
      const byDate = {}

      for (const row of this.activityRaw) {
        if (!byDate[row.date]) {
          byDate[row.date] = { total: 0, byKey: {} }
        }
        const count = parseInt(row.count)
        byDate[row.date].total += count
        byDate[row.date].byKey[row.api_key_name] = (byDate[row.date].byKey[row.api_key_name] || 0) + count
      }

      const keyNames = this.uniqueKeyNames

      return Object.entries(byDate)
        .map(([date, data]) => ({
          date,
          total: data.total,
          segments: keyNames.map(name => ({ name, count: data.byKey[name] || 0 }))
        }))
        .sort((a, b) => a.date.localeCompare(b.date))
    },
    maxDayCount () {
      if (this.activityDays.length === 0) return 1
      return Math.max(...this.activityDays.map(d => d.total), 1)
    }
  },
  watch: {
    refreshKey: 'loadData'
  },
  mounted () {
    this.loadData()
  },
  methods: {
    async loadData () {

      const params = {}
      if (this.from) params.from = this.from
      if (this.to) params.to = this.to
      const headers = { 'application-unique-title': this.application }

      try {
        const [overviewRes, keysRes, opsRes, activityRes, endpointsRes, recentRes] = await Promise.all([
          axios.get(`${VUE_APP_PROXY_SERVER_URL}/analytics/api/overview`, { headers, params }),
          axios.get(`${VUE_APP_PROXY_SERVER_URL}/analytics/api/keys`, { headers, params }),
          axios.get(`${VUE_APP_PROXY_SERVER_URL}/analytics/api/operations`, { headers, params }),
          axios.get(`${VUE_APP_PROXY_SERVER_URL}/analytics/api/activity`, { headers, params }),
          axios.get(`${VUE_APP_PROXY_SERVER_URL}/analytics/api/endpoints`, { headers, params }),
          axios.get(`${VUE_APP_PROXY_SERVER_URL}/analytics/api/recent`, { headers, params: { ...params, limit: 50 } })
        ])

        this.overview = overviewRes.data || this.overview
        this.keyBreakdown = keysRes.data || []
        this.operationBreakdown = opsRes.data || []
        this.activityRaw = activityRes.data || []
        this.topEndpoints = endpointsRes.data || []
        this.recentRequests = recentRes.data || []
      } catch (err) {
        console.log('failed to load API analytics', err)
      }
    },

    formatNumber (n) {
      return Number(n || 0).toLocaleString()
    },

    errorRate (row) {
      const total = parseInt(row.request_count) || 0
      const errors = parseInt(row.error_count) || 0
      if (total === 0) return 0
      return (errors / total) * 100
    },

    opColor (operation) {
      if (!operation) return 'gray'
      if (operation === 'create') return 'green'
      if (operation === 'read') return 'blue'
      if (operation === 'update') return 'yellow'
      if (operation === 'delete') return 'red'
      return 'gray'
    },

    segmentColor (index) {
      return COLORS[index % COLORS.length]
    },

    segmentHeight (count) {
      if (!this.maxDayCount) return '0%'
      return Math.max((count / this.maxDayCount) * 100, 1) + '%'
    },

    formatDate (dateStr) {
      const d = new Date(dateStr + 'T00:00:00')
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    },

    formatTime (isoDate) {
      if (!isoDate) return '–'
      const d = new Date(isoDate)
      return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' })
    }
  }
}
</script>

<style scoped>

.analytics-panel {
  max-width: 960px;
}

.analytics-section {
  margin-bottom: 28px;
}

.analytics-section-title {
  font-size: 0.78rem;
  font-weight: 600;
  color: #a0a09e;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 10px;
}

.analytics-empty {
  color: #a8a8a4;
  font-size: 0.8rem;
  padding: 12px 0;
}

/* ── Stat cards ── */
.stat-cards {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.stat-card {
  background: #1a1a17;
  border: 1px solid #2a2a27;
  border-radius: 6px;
  padding: 12px 16px;
  min-width: 120px;
}

.stat-card-success {
  border-color: rgba(108, 198, 68, 0.3);
}

.stat-card-success .stat-card-value {
  color: #6cc644;
}

.stat-card-error {
  border-color: rgba(224, 80, 80, 0.3);
}

.stat-card-error .stat-card-value {
  color: #e05050;
}

.stat-card-value {
  font-size: 1.3rem;
  font-weight: 700;
  color: #e8e8e6;
  line-height: 1.2;
}

.stat-unit {
  font-size: 0.7rem;
  font-weight: 400;
  color: #707070;
  margin-left: 2px;
}

.stat-card-label {
  font-size: 0.7rem;
  color: #707070;
  margin-top: 2px;
}

/* ── Tables ── */
.analytics-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;
}

.analytics-table th {
  text-align: left;
  color: #707070;
  font-weight: 500;
  padding: 6px 10px;
  border-bottom: 1px solid #2a2a27;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.analytics-table td {
  padding: 7px 10px;
  color: #a0a09e;
  border-bottom: 1px solid #1e1e1b;
}

.analytics-table tr:hover td {
  background: #1a1a17;
}

.analytics-table-compact td {
  padding: 5px 8px;
  font-size: 0.75rem;
}

.analytics-table-compact th {
  padding: 5px 8px;
}

.row-error td {
  background: rgba(224, 80, 80, 0.04);
}

.error-text {
  color: #e05050;
}

/* ── Badges ── */
.key-name-badge {
  font-size: 0.72rem;
  color: #4a9eff;
  background: rgba(74, 158, 255, 0.1);
  padding: 2px 7px;
  border-radius: 3px;
  font-weight: 500;
}

.op-badge {
  font-size: 0.68rem;
  padding: 2px 7px;
  border-radius: 3px;
  font-weight: 500;
}

.op-green { color: #6cc644; background: rgba(108, 198, 68, 0.1); }
.op-blue { color: #4a9eff; background: rgba(74, 158, 255, 0.1); }
.op-yellow { color: #e8a020; background: rgba(232, 160, 32, 0.1); }
.op-red { color: #e05050; background: rgba(224, 80, 80, 0.1); }
.op-gray { color: #707070; background: rgba(112, 112, 112, 0.1); }

.method-badge {
  font-size: 0.68rem;
  padding: 2px 6px;
  border-radius: 3px;
  font-weight: 600;
  font-family: monospace;
}

.method-get { color: #4a9eff; background: rgba(74, 158, 255, 0.1); }
.method-post { color: #6cc644; background: rgba(108, 198, 68, 0.1); }
.method-put { color: #e8a020; background: rgba(232, 160, 32, 0.1); }
.method-delete { color: #e05050; background: rgba(224, 80, 80, 0.1); }

.status-badge {
  font-size: 0.68rem;
  padding: 2px 6px;
  border-radius: 3px;
  font-weight: 600;
  font-family: monospace;
}

.status-ok {
  color: #6cc644;
  background: rgba(108, 198, 68, 0.1);
}

.status-error {
  color: #e05050;
  background: rgba(224, 80, 80, 0.1);
}

.time-cell {
  white-space: nowrap;
  color: #a8a8a4 !important;
  font-size: 0.7rem !important;
}

.path-cell {
  font-family: monospace;
  font-size: 0.72rem !important;
  word-break: break-all;
  max-width: 300px;
}

/* ── Histogram ── */
.histogram-wrap {
  overflow-x: auto;
}

.histogram {
  display: flex;
  align-items: flex-end;
  min-height: 180px;
  gap: 0;
}

.histogram-y-axis {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
  height: 160px;
  padding-right: 8px;
  flex-shrink: 0;
  border-right: 1px solid #2a2a27;
}

.histogram-y-label {
  font-size: 0.62rem;
  color: #a8a8a4;
  line-height: 1;
}

.histogram-bars {
  display: flex;
  align-items: flex-end;
  gap: 3px;
  flex: 1;
  height: 160px;
  padding-left: 6px;
  border-bottom: 1px solid #2a2a27;
}

.histogram-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  min-width: 28px;
  max-width: 60px;
  height: 100%;
}

.histogram-stack {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  width: 100%;
  flex: 1;
}

.histogram-segment {
  width: 100%;
  transition: height 0.3s ease;
  min-height: 0;
}

.histogram-segment:first-child {
  border-radius: 2px 2px 0 0;
}

.histogram-x-label {
  font-size: 0.6rem;
  color: #a8a8a4;
  margin-top: 5px;
  white-space: nowrap;
  flex-shrink: 0;
}

.chart-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 10px;
  padding-left: 40px;
}

.legend-item {
  font-size: 0.68rem;
  color: #707070;
  display: flex;
  align-items: center;
  gap: 4px;
}

.legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 2px;
}
</style>
